import { useState } from 'react';
import { api, ApiError, classCalendarUrl, type ClassSession } from '../api';

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
  });

const length = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h} hour${h === 1 ? '' : 's'}` : null, m ? `${m} min` : null].filter(Boolean).join(' ');
};

/**
 * A live class, as the trainee sees it: when it is, where, how many places are
 * left, and a button to take one. Attendance is still the instructor's to mark
 * — booking a place does not finish the unit.
 */
export function ClassUnit({ courseId, unitId, contentHtml, sessions, onChanged }: {
  courseId: number;
  unitId: number;
  contentHtml: string | null;
  sessions: ClassSession[];
  onChanged: (sessions: ClassSession[]) => void;
}) {
  const [busy, setBusy] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const act = async (session: ClassSession, kind: 'book' | 'cancel') => {
    if (kind === 'cancel' && !confirm(`Give up your place on "${session.name}"?`)) return;
    setBusy(session.id); setError(null);
    const path = `/courses/${courseId}/units/${unitId}/sessions/${session.id}/register`;
    try {
      const res = kind === 'book'
        ? await api.post<{ sessions: ClassSession[] }>(path)
        : await api.del<{ sessions: ClassSession[] }>(path);
      onChanged(res.sessions);
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(0); }
  };

  const booked = sessions.find((x) => x.mine);
  const upcoming = sessions.filter((x) => !x.started);
  const finished = sessions.filter((x) => x.started);

  return (
    <>
      {contentHtml && <div className="prose" dangerouslySetInnerHTML={{ __html: contentHtml }} />}

      {error && <p className="auth__error">{error}</p>}

      {booked && !booked.started && (
        <p className="notice">
          You have a place on <strong>{booked.name}</strong>, {when(booked.startsAt)}.{' '}
          <a href={classCalendarUrl(courseId, unitId, booked.id)}>Add it to your calendar</a>.
          {booked.conferenceUrl && <> Join at <a href={booked.conferenceUrl} target="_blank" rel="noreferrer">the class link</a> when it starts.</>}
        </p>
      )}

      {sessions.length === 0 && <p className="muted">No classes have been scheduled yet.</p>}

      {upcoming.length > 0 && <h3 className="h3">Classes you can book</h3>}
      <ul className="classes">
        {upcoming.map((x) => (
          <li key={x.id} className={`classes__row ${x.mine ? 'is-mine' : ''}`}>
            <div className="classes__main">
              <strong>{x.name}</strong>
              <div className="muted small">
                {when(x.startsAt)} · {length(x.durationMinutes)}
                {x.location ? ` · ${x.location}` : ''}
                {x.instructorName ? ` · with ${x.instructorName}` : ''}
              </div>
              {x.description && <div className="muted small">{x.description}</div>}
              <div className="muted small">
                {x.placesLeft == null
                  ? `${x.taken} booked`
                  : x.placesLeft === 0
                    ? 'Full'
                    : `${x.placesLeft} place${x.placesLeft === 1 ? '' : 's'} left of ${x.capacity}`}
              </div>
            </div>

            <div className="row">
              {x.mine && <span className="pill pill--completed">your place</span>}
              {x.canBook && (
                <button className="btn btn--primary" disabled={busy === x.id} onClick={() => void act(x, 'book')}>
                  {busy === x.id ? 'Booking…' : 'Book a place'}
                </button>
              )}
              {x.canCancel && (
                <button className="btn btn--ghost" disabled={busy === x.id} onClick={() => void act(x, 'cancel')}>
                  Give up my place
                </button>
              )}
              {!x.mine && !x.canBook && x.full && <span className="pill pill--incomplete">full</span>}
            </div>
          </li>
        ))}
      </ul>

      {finished.length > 0 && (
        <>
          <h3 className="h3">Past classes</h3>
          <ul className="classes">
            {finished.map((x) => (
              <li key={x.id} className="classes__row is-past">
                <div className="classes__main">
                  <strong>{x.name}</strong>
                  <div className="muted small">{when(x.startsAt)}{x.location ? ` · ${x.location}` : ''}</div>
                </div>
                {x.mine && (
                  <span className={`pill ${x.attended === true ? 'pill--completed' : x.attended === false ? 'pill--failed' : 'pill--pending_review'}`}>
                    {x.attended === true ? 'attended' : x.attended === false ? 'marked absent' : 'register not taken yet'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="muted small">
        Booking holds your place. This unit is completed by your instructor when they take the register.
      </p>
    </>
  );
}
